import { useState, useCallback } from 'react';
import { 
  AIProductService, 
  AIProductData, 
  AIProductRequest, 
  AIBatchResponse, 
  AIServiceError,
  AIDataProcessor 
} from '../services/aiProductService';

export interface AIFieldState {
  data: AIProductData | null;
  loading: boolean;
  error: string | null;
  lastUpdated: Date | null;
}

export interface AIProductState {
  manufacturer: AIFieldState;
  originCountry: AIFieldState;
  hsCode: AIFieldState;
  batchLoading: boolean;
  batchError: string | null;
}

const initialFieldState: AIFieldState = {
  data: null,
  loading: false,
  error: null,
  lastUpdated: null,
};

const initialState: AIProductState = {
  manufacturer: { ...initialFieldState },
  originCountry: { ...initialFieldState },
  hsCode: { ...initialFieldState },
  batchLoading: false,
  batchError: null,
};

export const useAIProductData = () => {
  const [state, setState] = useState<AIProductState>(initialState);

  // Helper function to update a specific field state
  const updateFieldState = useCallback((
    field: 'manufacturer' | 'originCountry' | 'hsCode',
    update: Partial<AIFieldState>
  ) => {
    setState(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        ...update,
      },
    }));
  }, []);

  // Clear all AI data
  const clearAllData = useCallback(() => {
    setState(initialState);
  }, []);

  // Clear specific field data
  const clearFieldData = useCallback((field: 'manufacturer' | 'originCountry' | 'hsCode') => {
    updateFieldState(field, {
      data: null,
      error: null,
      lastUpdated: null,
    });
  }, [updateFieldState]);

  // Get manufacturer data
  const getManufacturer = useCallback(async (productData: AIProductRequest) => {
    const validation = AIDataProcessor.validateProductData(productData);
    if (!validation.isValid) {
      updateFieldState('manufacturer', {
        error: validation.errors.join(', '),
        loading: false,
      });
      return null;
    }

    updateFieldState('manufacturer', {
      loading: true,
      error: null,
    });

    try {
      const result = await AIProductService.getManufacturer(productData);
      const sanitizedResult = {
        ...result,
        value: AIDataProcessor.sanitizeValue(result.value),
      };

      updateFieldState('manufacturer', {
        data: sanitizedResult,
        loading: false,
        error: null,
        lastUpdated: new Date(),
      });

      return sanitizedResult;
    } catch (error) {
      const aiError = error as AIServiceError;
      updateFieldState('manufacturer', {
        loading: false,
        error: aiError.message,
      });
      return null;
    }
  }, [updateFieldState]);

  // Get origin country data
  const getOriginCountry = useCallback(async (productData: AIProductRequest, manufacturer?: string) => {
    const validation = AIDataProcessor.validateProductData(productData);
    if (!validation.isValid) {
      updateFieldState('originCountry', {
        error: validation.errors.join(', '),
        loading: false,
      });
      return null;
    }

    updateFieldState('originCountry', {
      loading: true,
      error: null,
    });

    try {
      const result = await AIProductService.getOriginCountry(productData, manufacturer);
      const sanitizedResult = {
        ...result,
        value: AIDataProcessor.extractCountryCode(result.value),
      };

      updateFieldState('originCountry', {
        data: sanitizedResult,
        loading: false,
        error: null,
        lastUpdated: new Date(),
      });

      return sanitizedResult;
    } catch (error) {
      const aiError = error as AIServiceError;
      updateFieldState('originCountry', {
        loading: false,
        error: aiError.message,
      });
      return null;
    }
  }, [updateFieldState]);

  // Get HS code data
  const getHSCode = useCallback(async (productData: AIProductRequest) => {
    const validation = AIDataProcessor.validateProductData(productData);
    if (!validation.isValid) {
      updateFieldState('hsCode', {
        error: validation.errors.join(', '),
        loading: false,
      });
      return null;
    }

    updateFieldState('hsCode', {
      loading: true,
      error: null,
    });

    try {
      const result = await AIProductService.getHSCode(productData);
      const sanitizedResult = {
        ...result,
        value: AIDataProcessor.sanitizeValue(result.value),
      };

      updateFieldState('hsCode', {
        data: sanitizedResult,
        loading: false,
        error: null,
        lastUpdated: new Date(),
      });

      return sanitizedResult;
    } catch (error) {
      const aiError = error as AIServiceError;
      updateFieldState('hsCode', {
        loading: false,
        error: aiError.message,
      });
      return null;
    }
  }, [updateFieldState]);

  // Get all data at once (batch operation)
  const getAllData = useCallback(async (productData: AIProductRequest) => {
    const validation = AIDataProcessor.validateProductData(productData);
    if (!validation.isValid) {
      setState(prev => ({
        ...prev,
        batchError: validation.errors.join(', '),
        batchLoading: false,
      }));
      return null;
    }

    setState(prev => ({
      ...prev,
      batchLoading: true,
      batchError: null,
      manufacturer: { ...prev.manufacturer, loading: true, error: null },
      originCountry: { ...prev.originCountry, loading: true, error: null },
      hsCode: { ...prev.hsCode, loading: true, error: null },
    }));

    try {
      const result = await AIProductService.getAllProductData(productData);
      
      const sanitizedResult: AIBatchResponse = {
        manufacturer: {
          ...result.manufacturer,
          value: AIDataProcessor.sanitizeValue(result.manufacturer.value),
        },
        originCountry: {
          ...result.originCountry,
          value: AIDataProcessor.extractCountryCode(result.originCountry.value),
        },
        hsCode: {
          ...result.hsCode,
          value: AIDataProcessor.sanitizeValue(result.hsCode.value),
        },
      };

      const now = new Date();
      setState(prev => ({
        ...prev,
        batchLoading: false,
        batchError: null,
        manufacturer: {
          data: sanitizedResult.manufacturer,
          loading: false,
          error: null,
          lastUpdated: now,
        },
        originCountry: {
          data: sanitizedResult.originCountry,
          loading: false,
          error: null,
          lastUpdated: now,
        },
        hsCode: {
          data: sanitizedResult.hsCode,
          loading: false,
          error: null,
          lastUpdated: now,
        },
      }));

      return sanitizedResult;
    } catch (error) {
      const aiError = error as AIServiceError;
      setState(prev => ({
        ...prev,
        batchLoading: false,
        batchError: aiError.message,
        manufacturer: { ...prev.manufacturer, loading: false },
        originCountry: { ...prev.originCountry, loading: false },
        hsCode: { ...prev.hsCode, loading: false },
      }));
      return null;
    }
  }, []);

  // Check if any field is currently loading
  const isLoading = state.manufacturer.loading || state.originCountry.loading || state.hsCode.loading || state.batchLoading;

  // Check if any field has data
  const hasAnyData = !!(state.manufacturer.data || state.originCountry.data || state.hsCode.data);

  // Get confidence summary
  const getConfidenceSummary = useCallback(() => {
    const confidences: number[] = [];
    if (state.manufacturer.data) confidences.push(state.manufacturer.data.confidence);
    if (state.originCountry.data) confidences.push(state.originCountry.data.confidence);
    if (state.hsCode.data) confidences.push(state.hsCode.data.confidence);
    
    if (confidences.length === 0) return null;
    
    const average = confidences.reduce((sum, conf) => sum + conf, 0) / confidences.length;
    const lowest = Math.min(...confidences);
    
    return {
      average: Math.round(average),
      lowest,
      description: AIDataProcessor.getConfidenceDescription(average),
      color: AIDataProcessor.getConfidenceColor(average),
    };
  }, [state.manufacturer.data, state.originCountry.data, state.hsCode.data]);

  return {
    state,
    actions: {
      getManufacturer,
      getOriginCountry,
      getHSCode,
      getAllData,
      clearAllData,
      clearFieldData,
    },
    computed: {
      isLoading,
      hasAnyData,
      getConfidenceSummary,
    },
    utils: {
      isConfidenceAcceptable: AIDataProcessor.isConfidenceAcceptable,
      formatAIResponse: AIDataProcessor.formatAIResponse,
      getConfidenceDescription: AIDataProcessor.getConfidenceDescription,
      getConfidenceColor: AIDataProcessor.getConfidenceColor,
    },
  };
}; 